import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Bed, Bath, Square, ArrowRight, Eye, Loader2 } from 'lucide-react';
import { imageUrl } from '../lib/api';
import { VirtualTourModal } from './VirtualTourModal';

export const PropertiesSection = ({ t, properties, lang, loading }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  return (
    <section id="properties" className="py-32 bg-black relative overflow-hidden">
      {/* Decorative grid background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(197, 160, 94, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(197, 160, 94, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-block px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            Portfolio
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {t.properties.title}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
        </div>

        {/* Properties Grid - Premium Cards */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-12 h-12 mx-auto text-[#C5A05E] animate-spin" />
            <p className="text-gray-400 mt-4">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No properties available</div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {properties.filter(p => p.featured).map((property, index) => (
            <div
              key={property.id}
              className="group relative animate-fadeInUp"
              style={{ animationDelay: `${0.3 + index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E]/20 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 rounded-3xl" />
              
              <Card className="relative backdrop-blur-xl bg-white/5 border border-white/10 hover:border-[#C5A05E]/50 overflow-hidden transition-all duration-500 group-hover:transform group-hover:scale-[1.02]">
                {/* Property Image with overlay */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={property.images && property.images.length > 0 ? imageUrl(property.images[0]) : 'https://placehold.co/800x600?text=No+Image'}
                    alt={property.title[lang]}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Dark gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  
                  {/* Badge */}
                  <div className="absolute top-6 right-6">
                    <span className={`px-4 py-2 rounded-full text-sm font-bold text-white backdrop-blur-xl border ${
                      property.type === 'sale'
                        ? 'bg-[#C5A05E]/90 border-[#C5A05E]/50'
                        : 'bg-white/10 border-white/30'
                    }`}>
                      {property.type === 'sale' ? t.properties.forSale : t.properties.forRent}
                    </span>
                  </div>
                  
                  {/* View Details Overlay - Appears on Hover */}
                  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                    <Button
                      onClick={() => setSelectedProperty(property)}
                      className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] hover:from-[#D4B06E] hover:to-[#C5A05E] text-white px-8 py-6 text-lg font-bold rounded-2xl transform scale-90 group-hover:scale-100 transition-transform duration-300 group/btn"
                      data-testid={`view-property-${property.id}`}
                    >
                      <Eye className="mr-2 w-5 h-5" />
                      {t.properties.viewDetails}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                  
                  {/* Price Badge - Bottom Left */}
                  <div className="absolute bottom-6 left-6">
                    <div className="backdrop-blur-xl bg-black/60 border border-white/20 rounded-2xl px-6 py-3">
                      <div className="text-3xl font-bold text-white">
                        €{property.price.toLocaleString()}
                        {property.type === 'rent' && <span className="text-lg font-normal text-gray-300">/muaj</span>}
                      </div>
                    </div>
                  </div>
                </div>

                <CardContent className="p-8 space-y-6">
                  {/* Title & Location */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#C5A05E] transition-colors duration-300">
                      {property.title[lang]}
                    </h3>
                    <div className="flex items-center text-gray-400">
                      <MapPin className="w-4 h-4 mr-2 text-[#C5A05E]" />
                      {property.location[lang]}
                    </div>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                    <div className="flex flex-col items-center gap-2 p-4 backdrop-blur-xl bg-white/5 rounded-xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors group/feature">
                      <Bed className="w-6 h-6 text-[#C5A05E] group-hover/feature:scale-110 transition-transform" />
                      <span className="text-white font-semibold">{property.bedrooms}</span>
                      <span className="text-xs text-gray-400">{t.properties.bedrooms}</span>
                    </div>
                    <div className="flex flex-col items-center gap-2 p-4 backdrop-blur-xl bg-white/5 rounded-xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors group/feature">
                      <Bath className="w-6 h-6 text-[#C5A05E] group-hover/feature:scale-110 transition-transform" />
                      <span className="text-white font-semibold">{property.bathrooms}</span>
                      <span className="text-xs text-gray-400">{t.properties.bathrooms}</span>
                    </div>
                    <div className="flex flex-col items-center gap-2 p-4 backdrop-blur-xl bg-white/5 rounded-xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors group/feature">
                      <Square className="w-6 h-6 text-[#C5A05E] group-hover/feature:scale-110 transition-transform" />
                      <span className="text-white font-semibold">{property.area}</span>
                      <span className="text-xs text-gray-400">{t.properties.area}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed">{property.description[lang]}</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
        )}

        {/* View All Button - Premium */}
        <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.7s', opacity: 0, animationFillMode: 'forwards' }}>
          <Button
            size="lg"
            className="relative overflow-hidden bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white px-12 py-7 text-lg font-bold rounded-2xl shadow-2xl shadow-[#C5A05E]/50 transition-all duration-500 hover:scale-110 hover:shadow-[#C5A05E]/70 group border-2 border-[#C5A05E]/50"
          >
            <span className="relative z-10 flex items-center">
              {t.properties.viewAll}
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          </Button>
        </div>
      </div>

      {/* Virtual Tour / Gallery Modal */}
      <VirtualTourModal
        open={!!selectedProperty}
        onClose={() => setSelectedProperty(null)}
        property={selectedProperty}
        lang={lang}
      />
    </section>
  );
};